import { log } from 'cc';
import Processor from '../../Components/Misc/Processor';
import StateContext from './StateContext';

const LOG = false;

export default abstract class StateScenario {
    private _enterCount = 0;
    private _stopScenarioCallback: (waitForFinish?: boolean) => Promise<void> = null;

    constructor(protected readonly context: StateContext) {}

    public get isCurrent() {
        return !!this.enterCount;
    }

    protected get isReentering() {
        return this.enterCount > 1;
    }

    private get isRunning() {
        return !!this._stopScenarioCallback;
    }

    private get enterCount() {
        return this._enterCount;
    }
    private set enterCount(count: number) {
        if (count < 0) {
            throw new Error('Enter count must be non-negative');
        }
        if (count === this._enterCount) {
            return;
        }
        if (LOG) {
            log(`new enterCount ${count}`);
        }
        this._enterCount = count;
    }

    /** enter this state scenario */
    public enter() {
        this.enterCount++;
        if (LOG) {
            log(`enter ${this.constructor.name}, isReenter: ${this.enterCount > 1}`);
        }
        // don't need to reenter if state scenario is already finished
        if (this.isReentering && !this.isRunning) {
            return;
        }
        // reset state scenario before reentering
        if (this.isRunning) {
            this._reset();
        }
        [, this._stopScenarioCallback] = Processor.run(this.onEnter(), async () => {
            this._stopScenarioCallback = null;
        });
        // update state scenario immediately after entering state scenario
        this.onProgress();
    }

    /** update this state scenario */
    public progress() {
        if (!this.isCurrent) {
            throw new Error(`You must enter ${this.constructor.name} before progressing`);
        }
        if (LOG) {
            log(`progress ${this.constructor.name}`);
        }
        this.onProgress();
    }

    /** leave this state scenario */
    public exit() {
        if (!this.isCurrent) {
            throw new Error(`You must enter ${this.constructor.name} before exiting`);
        }
        if (LOG) {
            log(`exit ${this.constructor.name}`);
        }
        this._stop();
        this.onExit();
        this.enterCount = 0;
    }

    /** go to the end of this state scenario */
    public skip(): void {
        if (LOG) {
            log(`skip ${this.constructor.name}`);
        }
        const needToSkip = !this.isCurrent || this.isRunning;
        this.enterCount++;
        if (this.isRunning) {
            this._reset();
        }
        if (needToSkip) {
            this.onSkip();
        }
        this.onExit();
        this.enterCount = 0;
    }

    private _reset() {
        this._stop();
        this.onReset();
    }

    /** stop this state scenario */
    private _stop() {
        if (!this.isRunning) {
            return;
        }
        if (LOG) {
            log(`stop ${this.constructor.name}`);
        }
        this._stopScenarioCallback(false);
        this._stopScenarioCallback = null;
        this.onStop();
    }

    /** method invoked when enter this state scenario */
    protected abstract onEnter(): Generator<Promise<any>, any, any>;

    /** method invoked when event is received during this state scenario */
    protected abstract onProgress(): void;

    /** method invoked when exit this state scenario */
    protected abstract onExit(): void;

    /** method invoked when reenter this state scenario */
    protected abstract onReset(): void;

    /** method invoked when when state scenario is skipped */
    protected abstract onSkip(): void;

    /** method invoked when when this state scenario is stopped */
    protected abstract onStop(): void;
}
